import { Flex, Checkbox } from "@theorgchartdev/storybook";
import { DataSourceSetupComposite, enumATSModes } from "./DataSourceSetupComposite";
import AdditionalTableFieldToggleMenu from "../AdditionalTableFieldToggleMenu";
import {
  DATA_SOURCES_TEST_PREFIX,
  kKeyToLabelAndDefaultText,
  enumGreenhouseAuthModes,
  GetGreenhouseAuthMode,
  ShouldShowGreenhouseV1Toggle
} from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/Utils";
import { GreenhouseOAuthSetup } from "./GreenhouseOAuthSetup";
import { appcstr } from "@coreapp/utils/appcstr";

const DEFAULT_STATUSES = ["open", "closed", "draft"];

// partnerOAuth collects nothing by hand — the token pair arrives from the authorize popup.
const kAuthModeInputKeys = {
  [enumGreenhouseAuthModes.v1]: ["apiKey"],
  [enumGreenhouseAuthModes.clientCredentials]: ["clientID", "clientSecret"],
  [enumGreenhouseAuthModes.partnerOAuth]: []
};

const BuildInputs = authMode => {
  return kAuthModeInputKeys[authMode].map(key => ({
    dataKey: key,
    label: appcstr(kKeyToLabelAndDefaultText[key][0]),
    placeholder: appcstr(kKeyToLabelAndDefaultText[key][1]),
    passwordMask: ["apiKey", "clientSecret"].includes(key)
  }));
};

export const GreenhouseSetupComposite = ({ config, onChange, index, importType, chartConfig }) => {
  const useV1 = config?.useV1 !== false; // default true for existing customers
  const authMode = GetGreenhouseAuthMode(config);

  const discoveredStatuses = config.discoveredStatuses || [];
  const allStatuses = [...new Set([...DEFAULT_STATUSES, ...discoveredStatuses])];

  const statusOptions = allStatuses.map(status => ({
    value: status,
    label: status
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }));

  const selectedStatuses = config.statuses || ["open"];

  const handleStatusChange = option => {
    const status = option.value.value;
    let newStatuses = [...selectedStatuses];
    if (newStatuses.includes(status)) {
      if (newStatuses.length > 1) {
        newStatuses = newStatuses.filter(s => s !== status);
      }
    } else {
      newStatuses.push(status);
    }
    onChange({ ...config, statuses: newStatuses });
  };

  // Also shown in clientCredentials mode: that grant is not open to partners, so those customers
  // need a way off it. Connecting stores a refreshToken, which outranks their stale clientID.
  const AuthNode =
    authMode === enumGreenhouseAuthModes.v1 ? null : (
      <GreenhouseOAuthSetup value={config} onChange={patch => onChange({ ...config, ...patch })} />
    );

  return (
    <DataSourceSetupComposite
      config={config}
      onChange={onChange}
      index={index}
      chartConfig={chartConfig}
      showInputControls
      showConfiguration={importType !== "import"}
      showLoadHeaders={importType !== "import"}
      importType={importType}
      ATSType={enumATSModes.pureATS}
      doInputLookup={false}
      inputOptions={BuildInputs(authMode)}
      postInputControlsNode={AuthNode}
    >
      <Flex direction={"column"} gap={"12px"}>
        {ShouldShowGreenhouseV1Toggle(config) && (
          <Checkbox
            id={"use-v1-api"}
            data-testid={`${DATA_SOURCES_TEST_PREFIX}-greenhouse-v1-api-checkbox`}
            label={appcstr("greenhousev1label")}
            size={"medium"}
            checked={useV1}
            onChange={() => onChange({ ...config, useV1: !useV1 })}
          />
        )}
        <AdditionalTableFieldToggleMenu
          label={appcstr("genericstatus1")}
          dataTestId={`${DATA_SOURCES_TEST_PREFIX}-greenhouse-status-fields-menu`}
          allOptions={statusOptions}
          selectedOptions={selectedStatuses}
          onChange={handleStatusChange}
        />
      </Flex>
    </DataSourceSetupComposite>
  );
};
